// Copied from apps/admin/src/features/gps-stops/model/useGpsStopsLayer.ts (PRD #875).
import { useMemo, useState } from "react";
import {
  distanceMeters,
  isNearPolygons,
  pickupLocationPolygons,
} from "~/features/map-view/model/pickup-geofence";
import {
  GPS_STOP_TYPES,
  MIN_OFF_SITE_STOP_SECONDS,
  OFF_SITE_GPS_STOP_TYPES,
  type GpsStopType,
} from "./stop-types";
import { useGpsStops, type GpsStop, type UseGpsStopsArgs } from "./useGpsStops";

/** A pickup location the shown routes were planned to service. */
export interface PlannedSite {
  name: string;
  lat: number;
  lon: number;
  /** GeoJSON from PickupLocation.geometry; without it the site is its point. */
  geometry?: unknown;
}

export type UseGpsStopsLayerArgs = Omit<UseGpsStopsArgs, "includeUnmatched" | "enabled"> & {
  /** Whether the layer starts switched on. Default: on. */
  initiallyEnabled?: boolean;
  /**
   * Sites of the routes on screen. An unmatched stop standing on one of them is
   * shown as a stop at that site: the detector missed the match, but the truck
   * was exactly where the plan sent it.
   */
  plannedSites?: PlannedSite[];
};

/** Unmatched types a planned site can account for. */
const RECLASSIFIABLE_TYPES: readonly GpsStopType[] = ["UNKNOWN", "NO_NEARBY_PL"];
/** Slack past the geofence: an H3 cell centre or a dwell centroid, not the truck itself. */
const GEOFENCE_TOLERANCE_M = 25;
/** A site with no geofence is only its point, so the reach is wider. */
const POINT_TOLERANCE_M = 50;

const EMPTY_SITES: PlannedSite[] = [];

/**
 * Owns the GPS-stops layer state and the fetch behind it, so every map that
 * wants stops gets the same defaults and the same toggle semantics.
 *
 * Spread `legendProps` into `<GpsStopsLegend />` and `layerProps` into
 * `<GpsStopsLayer />`.
 */
export function useGpsStopsLayer({
  initiallyEnabled = true,
  plannedSites = EMPTY_SITES,
  ...args
}: UseGpsStopsLayerArgs) {
  const [enabled, setEnabled] = useState(initiallyEnabled);
  const [visibleTypes, setVisibleTypes] = useState<Set<GpsStopType>>(() => new Set(GPS_STOP_TYPES));

  // Always fetch every type, including the unmatched ones, and let the legend
  // filter what is drawn.
  //
  // Making include_unmatched part of the query key instead was a mistake: with
  // the unmatched types unchecked the server never sent them, so the legend
  // showed "0" next to types that simply had not been loaded -- indistinguishable
  // from genuinely having none. The saving was not worth it either: a route
  // window costs 2 KB vs 3 KB, six days 19 KB vs 33 KB. Toggling is now instant
  // rather than a refetch.
  const {
    stops: fetchedStops,
    isFetching,
    error,
  } = useGpsStops({
    ...args,
    includeUnmatched: true,
    enabled,
  });

  // Parsed once per site list, not per stop.
  const siteShapes = useMemo(
    () =>
      plannedSites
        .filter((site) => site.lat !== 0 || site.lon !== 0)
        .map((site) => ({ site, polygons: pickupLocationPolygons(site.geometry) })),
    [plannedSites],
  );

  // Reclassified here, before both the map and the legend, so the counts match what is
  // drawn; short dwells are dropped only after that, so a brief planned-site stop survives.
  const stops = useMemo<GpsStop[]>(() => {
    const reclassified =
      siteShapes.length === 0
        ? fetchedStops
        : fetchedStops.map((stop) => {
            if (!RECLASSIFIABLE_TYPES.includes(stop.type)) return stop;
            const match = siteShapes.find(({ site, polygons }) =>
              polygons.length > 0
                ? isNearPolygons(stop.lat, stop.lon, polygons, GEOFENCE_TOLERANCE_M)
                : distanceMeters(stop.lat, stop.lon, site.lat, site.lon) <= POINT_TOLERANCE_M,
            );
            return match
              ? { ...stop, type: "PICKUP_LOCATION_PLANNED" as const, siteName: match.site.name }
              : stop;
          });
    return reclassified.filter(
      (stop) =>
        !OFF_SITE_GPS_STOP_TYPES.includes(stop.type) ||
        stop.durationSeconds >= MIN_OFF_SITE_STOP_SECONDS,
    );
  }, [fetchedStops, siteShapes]);

  return {
    stops,
    isFetching,
    error,
    enabled,
    visibleTypes,
    legendProps: {
      enabled,
      onEnabledChange: setEnabled,
      visibleTypes,
      onVisibleTypesChange: setVisibleTypes,
      stops,
      isFetching,
    },
    layerProps: {
      stops: enabled ? stops : [],
      visibleTypes,
    },
  };
}
